export type OverlayCorner = 'tl' | 'tr' | 'bl' | 'br';
export type OverlayShape = 'circle' | 'rounded';

export interface OverlayConfig {
  corner: OverlayCorner;
  shape: OverlayShape;
  mirror: boolean;
  /** Width of the overlay as a fraction of the frame width. */
  scale: number;
  /** Gap from the frame edge as a fraction of the frame width. */
  margin: number;
}

const radius = 14;

function roundedPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  r: number,
) {
  const corner = Math.min(r, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + corner, y);
  ctx.lineTo(x + width - corner, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + corner);
  ctx.lineTo(x + width, y + height - corner);
  ctx.quadraticCurveTo(x + width, y + height, x + width - corner, y + height);
  ctx.lineTo(x + corner, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - corner);
  ctx.lineTo(x, y + corner);
  ctx.quadraticCurveTo(x, y, x + corner, y);
  ctx.closePath();
}

/**
 * Composites the captured screen with an optional camera inset. Frames are
 * drawn here only when an inset is present — otherwise the captured frame is
 * handed to the encoder untouched, which keeps the common path allocation-free.
 */
export class Compositor {
  readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private webcam: HTMLVideoElement | null = null;
  private width: number;
  private height: number;

  constructor(width: number, height: number) {
    this.width = width;
    this.height = height;
    this.canvas = document.createElement('canvas');
    this.canvas.width = width;
    this.canvas.height = height;
    const ctx = this.canvas.getContext('2d', { alpha: false, desynchronized: true });
    if (!ctx) throw new Error('Canvas 2D is unavailable, so the camera overlay cannot be composited.');
    this.ctx = ctx;
    this.ctx.imageSmoothingEnabled = true;
    this.ctx.imageSmoothingQuality = 'high';
  }

  get dimensions(): { width: number; height: number } {
    return { width: this.width, height: this.height };
  }

  setWebcam(element: HTMLVideoElement | null): void {
    this.webcam = element;
  }

  resize(width: number, height: number): void {
    if (this.width === width && this.height === height) return;
    this.width = width;
    this.height = height;
    this.canvas.width = width;
    this.canvas.height = height;
    this.ctx.imageSmoothingEnabled = true;
    this.ctx.imageSmoothingQuality = 'high';
  }

  draw(source: CanvasImageSource, overlay: OverlayConfig | null): void {
    const { ctx, width, height } = this;
    ctx.drawImage(source, 0, 0, width, height);
    if (overlay && this.webcam && this.webcam.readyState >= 2) {
      this.drawInset(overlay);
    }
  }

  private drawInset(overlay: OverlayConfig): void {
    const { ctx, width, height } = this;
    const webcam = this.webcam;
    if (!webcam) return;

    const sourceWidth = webcam.videoWidth || width;
    const sourceHeight = webcam.videoHeight || height;
    const aspect = sourceWidth / sourceHeight;

    const insetWidth = Math.round(width * overlay.scale);
    const insetHeight = Math.round(insetWidth / aspect);
    const gap = Math.round(width * overlay.margin);

    const x = overlay.corner === 'tr' || overlay.corner === 'br' ? width - insetWidth - gap : gap;
    const y = overlay.corner === 'tl' || overlay.corner === 'tr' ? gap : height - insetHeight - gap;

    ctx.save();
    ctx.beginPath();
    if (overlay.shape === 'circle') {
      const diameter = Math.min(insetWidth, insetHeight);
      const cx = x + insetWidth / 2;
      const cy = y + insetHeight / 2;
      ctx.arc(cx, cy, diameter / 2, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();
      // Cover the circle with a center-cropped square of the camera image.
      const scale = Math.max(diameter / sourceWidth, diameter / sourceHeight);
      const drawWidth = sourceWidth * scale;
      const drawHeight = sourceHeight * scale;
      this.drawMirrored(webcam, cx - drawWidth / 2, cy - drawHeight / 2, drawWidth, drawHeight, overlay.mirror);
    } else {
      roundedPath(ctx, x, y, insetWidth, insetHeight, Math.round(radius * (width / 1280)));
      ctx.clip();
      this.drawMirrored(webcam, x, y, insetWidth, insetHeight, overlay.mirror);
    }

    ctx.restore();

    // Hairline ring so the inset reads as intentional on any background.
    ctx.save();
    ctx.lineWidth = Math.max(2, Math.round(width / 640));
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.28)';
    if (overlay.shape === 'circle') {
      const diameter = Math.min(insetWidth, insetHeight);
      const cx = x + insetWidth / 2;
      const cy = y + insetHeight / 2;
      ctx.beginPath();
      ctx.arc(cx, cy, diameter / 2 - ctx.lineWidth / 2, 0, Math.PI * 2);
      ctx.stroke();
    } else {
      roundedPath(
        ctx,
        x + ctx.lineWidth / 2,
        y + ctx.lineWidth / 2,
        insetWidth - ctx.lineWidth,
        insetHeight - ctx.lineWidth,
        Math.round(radius * (width / 1280)),
      );
      ctx.stroke();
    }
    ctx.restore();
  }

  private drawMirrored(
    source: CanvasImageSource,
    x: number,
    y: number,
    width: number,
    height: number,
    mirror: boolean,
  ): void {
    const { ctx } = this;
    if (!mirror) {
      ctx.drawImage(source, x, y, width, height);
      return;
    }
    ctx.save();
    ctx.translate(x + width, y);
    ctx.scale(-1, 1);
    ctx.drawImage(source, 0, 0, width, height);
    ctx.restore();
  }
}
